import React from 'react';

import { ContactEntity } from '~/components/Contact';
import Button from '~/components/core/Atomic/Buttons/Button';
import Heading from '~/components/core/TextComponents/Heading';
import { CoinsIcon } from '~/components/icons';
import { getDateFromServerDateTime } from '~/DateTimeUtils';
import formatDaysAsWeeksAndDays from '~/Utils/formatDaysAsWeekAndDays';

import { AmountValueDisplay } from './helpers';
import IncomeProtectionFieldDisplay from './IncomeProtectionFieldDisplay';
import TEXTS from './texts';
import type { IncomeProtectionDASettingDisplayKeysType, IncomeProtectionDaSettingType } from './types';

interface IncomeProtectionSettingsDisplayProps {
  setting: IncomeProtectionDaSettingType;
  viewOnly: boolean;
  onEditClick: () => void;
}

const IncomeProtectionSettingsDisplayHeader = () => (
  <div className="grid justify-items-start gap-12">
    <div className="rounded-md bg-azure-100 p-[10px] text-azure-700">
      <CoinsIcon iconColor="currentColor" size={24} className="block" />
    </div>
    <Heading variant={Heading.TYPES.H2}>{TEXTS.paymentSettings}</Heading>
  </div>
);

const DISPLAY_FIELDS = [
  {
    key: 'start_date',
    label: TEXTS.settingDisplayLabels.start_date,
    displayValue: (setting: IncomeProtectionDaSettingType) => getDateFromServerDateTime(setting.start_date),
  },
  {
    key: 'benefit_period_num_of_days',
    label: TEXTS.settingDisplayLabels.benefit_period_num_of_days,
    displayValue: (setting: IncomeProtectionDaSettingType) => (
      <span className="capitalize">{formatDaysAsWeeksAndDays(setting.benefit_period_num_of_days)}</span>
    ),
  },
  {
    key: 'payment_frequency_num_of_days',
    label: TEXTS.settingDisplayLabels.payment_frequency_num_of_days,
    displayValue: (setting: IncomeProtectionDaSettingType) => TEXTS.valueAsDays(setting.payment_frequency_num_of_days),
  },
  {
    key: 'average_weekly_income',
    label: TEXTS.settingDisplayLabels.average_weekly_income,
    displayValue: (setting: IncomeProtectionDaSettingType) => (
      <AmountValueDisplay amount={setting.average_weekly_income} />
    ),
  },
  {
    key: 'weekly_payment_limit',
    label: TEXTS.settingDisplayLabels.weekly_payment_limit,
    displayValue: (setting: IncomeProtectionDaSettingType) => (
      <AmountValueDisplay amount={setting.weekly_payment_limit} />
    ),
  },
  {
    key: 'claimant_weekly_limit',
    label: TEXTS.settingDisplayLabels.claimant_weekly_limit,
    displayValue: (setting: IncomeProtectionDaSettingType) => (
      <AmountValueDisplay amount={setting.claimant_weekly_limit} />
    ),
  },
  {
    key: 'payee_contact_id',
    label: TEXTS.settingDisplayLabels.payee_contact_id,
    displayValue: (setting: IncomeProtectionDaSettingType) => (
      <ContactEntity contactId={setting.payee_contact_id} contactDisplayName={setting.payee_contact_full_name} />
    ),
  },
] as const satisfies {
  key: IncomeProtectionDASettingDisplayKeysType;
  label: string;
  displayValue: (setting: IncomeProtectionDaSettingType) => React.ReactNode;
}[];

const IncomeProtectionSettingsDisplay = ({ setting, onEditClick, viewOnly }: IncomeProtectionSettingsDisplayProps) => {
  return (
    <div className="sticky top-0 flex flex-col gap-24 self-start rounded-xl border border-solid border-slate-400 bg-slate-100 p-24">
      <IncomeProtectionSettingsDisplayHeader />
      {DISPLAY_FIELDS.map((field) => (
        <IncomeProtectionFieldDisplay key={field.key} label={field.label} valueDisplay={field.displayValue(setting)} />
      ))}
      {viewOnly ? null : (
        <Button onClick={onEditClick} variant="outlined" color="primary" className="mt-24 bg-white">
          {TEXTS.edit}
        </Button>
      )}
    </div>
  );
};

export default IncomeProtectionSettingsDisplay;
